// Real verified F&B permit data by city — 2025 official government sources

import { PermitDetail } from "./businessData";

export interface CityPermitConfig {
  city: string;
  state: string;
  lastVerified: string;
  permits: PermitDetail[];
}

function p(name: string, simple: string, required: boolean, link: string, processingTime: string, estimatedCost: string, requirements: string[]): PermitDetail {
  return { name, simple, required, link, processingTime, estimatedCost, requirements };
}

const EIN = (required = true) => p(
  "EIN (Federal Tax ID)",
  "Free federal tax ID — required for all US businesses. Apply online in minutes.",
  required,
  "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online",
  "Instant online",
  "FREE",
  ["SSN or ITIN of responsible party", "Business name and address", "Business structure type"]
);

const CITY_PERMITS: Record<string, CityPermitConfig> = {
  miami: {
    city: "Miami", state: "FL", lastVerified: "2025",
    permits: [
      EIN(),
      p("Florida LLC / Corporation Registration", "Register your business entity with the state of Florida via Sunbiz.", true,
        "https://efile.sunbiz.org/llc_file.html", "3–5 business days", "$125 (LLC) / $70 (Corp)",
        ["Articles of Organization", "Registered agent in FL", "Filing fee payment", "Annual renewal: $138.75 LLC"]),
      p("Fictitious Name (DBA)", "Required if your business operates under a different name than your LLC.", true,
        "https://efile.sunbiz.org/ficregintro.html", "3–5 business days", "$50",
        ["LLC or Corp must be registered first", "Trade name you want to use", "Renews every 5 years"]),
      p("Sales Tax Registration (FL Dept of Revenue)", "Register to collect Florida's 7% sales tax (6% state + 1% Miami-Dade surtax).", true,
        "https://taxapps.floridarevenue.com/TaxRegistration/Account/Register", "5–7 business days", "FREE",
        ["EIN", "Business address", "Expected monthly sales volume"]),
      p("Miami-Dade Business Tax Receipt", "Required operating license for all businesses in Miami-Dade County.", true,
        "https://www.miamidade.gov/global/economy/business/business-tax-receipt.page", "30–60 days", "$45–$300/yr",
        ["Proof of business registration", "Location address", "Business type classification", "Renews annually (Sep 30)"]),
      p("Food Service License — Nonseating (DBPR)", "For take-out, delivery, ghost kitchens with no dine-in seating.", true,
        "https://www.myfloridalicense.com/intentions2.asp?chession=&catid=3&divid=2", "30–45 days (inspection required)", "$242/yr + $50 application fee",
        ["Completed DBPR application", "Floor plan", "Equipment list", "Passed health inspection"]),
      p("Food Service License — Seating 1–49 (DBPR)", "Full-service restaurant license for up to 49 dine-in seats.", true,
        "https://www.myfloridalicense.com/intentions2.asp?chession=&catid=3&divid=2", "30–60 days (inspection required)", "$262/yr + $50 application fee",
        ["DBPR application", "Seating layout plan", "Menu", "Health inspection passed"]),
      p("Food Service License — Seating 50–149 (DBPR)", "Restaurant license for 50–149 seats — larger venues.", false,
        "https://www.myfloridalicense.com/intentions2.asp?chession=&catid=3&divid=2", "30–60 days", "$273/yr + $50 application fee",
        ["DBPR application", "Detailed floor plan", "Fire safety compliance", "Health inspection"]),
      p("Alcohol License — Beer & Wine (2APS)", "Allows beer and wine on-premise consumption. No liquor. Issued by FL Division of Alcoholic Beverages and Tobacco.", false,
        "https://www.myfloridalicense.com/DBPR/alcoholic-beverages-and-tobacco/", "4–8 weeks", "$400/yr approx (varies by county)",
        ["DABT application", "Background check", "Proof of premises", "No quota limit"]),
      p("Alcohol License — 2COP Full Liquor Restaurant", "FL Division of Alcoholic Beverages and Tobacco — 2COP licence for beer, wine & spirits for restaurants. $1,820/year.", false,
        "https://www.myfloridalicense.com/DBPR/alcoholic-beverages-and-tobacco/", "4–8 weeks", "$1,820/yr",
        ["DABT application & background check", "Proof of premises", "51%+ food revenue requirement", "⚠️ Verify link: official .gov URLs may change"]),
      p("Alcohol License — Full Liquor Quota (4COP)", "For bars/nightclubs. Limited by county population quota — must buy from existing holder or win lottery.", false,
        "https://www2.myfloridalicense.com/alcoholic-beverages-and-tobacco/quota-license-information/", "90–120 days", "$1,820/yr + market purchase $20K–$400K",
        ["Must purchase from existing license holder OR win state lottery", "Background check", "Financial disclosure"]),
      p("Certificate of Occupancy", "Confirms building meets fire, safety, and zoning codes before opening.", true,
        "https://www.miamidade.gov/permits/", "60–90 days", "$100–$500",
        ["Building inspection passed", "Fire inspection passed", "Zoning compliance verified", "ADA compliance"]),
      p("Zoning Verification / Use Permit", "Confirms your location is zoned for food service — check before signing any lease.", true,
        "https://www.miamidade.gov/global/economy/zoning/home.page", "30–60 days", "$200–$800",
        ["Property address", "Intended business use", "Check BEFORE signing lease"]),
      p("Health Permit (FL DOH Miami-Dade — Food Hygiene)", "County health permit required for food handling operations in Miami-Dade.", true,
        "https://miamidade.floridahealth.gov/programs-and-services/environmental-health/food-hygiene/index.html", "30–45 days", "$200–$500/yr",
        ["Food handling procedures", "Equipment list", "Employee health policies", "Annual renewal"]),
      p("Sign Permit", "Required for any exterior business signage.", true,
        "https://www.miamidade.gov/permits/", "15–30 days", "$75–$400",
        ["Sign design with dimensions", "Building plans", "Property owner approval"]),
      p("Food Manager Certification (CFM)", "At least 1 Certified Food Manager must be on-site at all times.", true,
        "https://www.myfloridalicense.com/intentions2.asp?chession=&catid=3&divid=2", "Exam: schedule anytime", "$100–$200 (exam fee)",
        ["Pass approved food safety exam (ServSafe)", "Renews every 5 years", "Must be present during all operating hours"]),
    ]
  },

  "new york": {
    city: "New York City", state: "NY", lastVerified: "2025",
    permits: [
      EIN(),
      p("NY LLC / Corporation Registration", "Register your business entity with New York State.", true,
        "https://www.dos.ny.gov/corps/llcguide.html", "7–10 business days", "$200 (LLC) + $9/yr biennial fee",
        ["Articles of Organization", "Publication requirement in 2 newspapers", "Registered agent in NY", "Biennial statement ($9)"]),
      p("DBA / Certificate of Assumed Name", "Required if operating under a trade name different from your LLC.", true,
        "https://www.nycourts.gov/", "5–10 business days", "$33–$34",
        ["File with county clerk", "LLC registration required first", "Renews every 5 years"]),
      p("Sales Tax Certificate of Authority", "Required to collect NY sales tax: 8.875% total in NYC.", true,
        "https://www.tax.ny.gov/bus/st/register.htm", "5–10 business days", "FREE",
        ["EIN", "Business address", "File quarterly returns"]),
      p("NYC Business Certificate", "General business certificate from NYC Dept of Consumer & Worker Protection.", true,
        "https://www.nyc.gov/site/dca/businesses/license-overview.page", "15–30 days", "$100–$200/yr",
        ["Business registration", "Location address", "Annual or biennial renewal"]),
      p("Food Service Establishment Permit (DOHMH)", "Core permit for ANY fixed-location food business in NYC.", true,
        "https://www.nyc.gov/site/doh/business/food-operators/restaurants.page", "30–60 days (inspection required)", "$280 (new application)",
        ["DOHMH application", "Floor plan", "Menu", "Equipment list", "Workers' comp proof", "Annual renewal ($280)"]),
      p("Food Protection Certificate (FPC)", "At least 1 certified supervisor must be on-site at ALL times.", true,
        "https://www.nyc.gov/site/doh/business/food-operators/food-protection-certificate.page", "Course: self-paced; Exam: scheduled", "$24 (exam fee; course is FREE)",
        ["Complete free NYC Health Academy online course", "Pass exam", "Renews every 5 years"]),
      p("Certificate of Occupancy", "Issued by NYC Dept of Buildings.", true,
        "https://www.nyc.gov/site/buildings/index.page", "60–120 days", "$280–$2,000+",
        ["Building inspection", "Fire safety compliance", "Zoning verification", "ADA compliance"]),
      p("Alcohol License — On-Premises (NY State Liquor Authority)", "NY State Liquor Authority on-premises licence. Covers beer, wine & spirits for restaurants.", false,
        "https://sla.ny.gov/apply-license", "8–12 weeks", "$4,352",
        ["SLA application", "Background check", "Financial disclosure", "Community board review", "2-year renewal", "⚠️ Verify link: official .gov URLs may change"]),
      p("Alcohol License — Beer & Wine Only (SLA)", "NY State Liquor Authority beer & wine on-premises licence.", false,
        "https://sla.ny.gov/apply-license", "8–12 weeks", "$330–$1,100/yr",
        ["SLA application", "Background check", "Community board notification", "2-year renewal"]),
      p("Fire Safety Permit (FDNY)", "Required for commercial cooking equipment, hood systems.", true,
        "https://www.nyc.gov/site/fdny/index.page", "30–60 days", "$105–$1,000",
        ["Fire suppression system", "Hood and duct inspection", "Annual renewal"]),
      p("Sign Permit", "Required for ALL exterior signs.", true,
        "https://www.nyc.gov/site/buildings/index.page", "15–30 days", "$85–$500",
        ["Sign design and dimensions", "Building owner approval", "Zoning compliance"]),
      p("Workers' Comp & Disability Insurance", "Proof required when applying for Food Service Establishment Permit.", true,
        "https://www.wcb.ny.gov/", "Before opening", "Varies (required for DOHMH permit)",
        ["Workers' compensation policy", "Disability insurance policy"]),
    ]
  },

  houston: {
    city: "Houston", state: "TX", lastVerified: "2025",
    permits: [
      EIN(),
      p("Texas LLC / Corporation Registration", "File with TX Secretary of State via SOSDirect.", true,
        "https://www.sos.state.tx.us/corp/forms_option.shtml", "3–5 business days", "$300 (LLC) / $300 (Corp)",
        ["Articles of Organization", "Registered agent in TX"]),
      p("DBA (Assumed Name Certificate)", "Required if using a trade name. File with Harris County Clerk.", true,
        "https://www.harriscountyclerk.com/", "2–5 business days", "$25 (Harris County)",
        ["LLC registration", "Trade name", "Renews every 10 years"]),
      p("Texas Sales & Use Tax Permit", "Required to collect Texas 8.25% sales tax.", true,
        "https://comptroller.texas.gov/taxes/sales/", "2–4 weeks", "FREE",
        ["EIN", "Business address"]),
      p("City of Houston Business License", "General operating license.", true,
        "https://www.houstontx.gov/ara/business-licensing.html", "10–20 business days", "$35–$200/yr",
        ["Business registration", "Location address", "Annual renewal"]),
      p("Food Establishment Permit (Houston Health Dept)", "Tier-based: Tier 1 $258, Tier 2 $388, Tier 3 $813/yr.", true,
        "https://www.houstontx.gov/health/Food-Inspection/food-establish-permits.html", "30–45 days (inspection required)", "$258–$813/yr",
        ["Health department application", "Floor plan", "Equipment list", "Menu"]),
      p("Food Manager Certification", "At least 1 Texas-certified food manager required.", true,
        "https://www.dshs.texas.gov/food-establishments/food-safety-training", "Exam: anytime", "$100–$200",
        ["Pass approved exam (ServSafe, Prometric)", "Renews every 5 years"]),
      p("Certificate of Occupancy (CO)", "Confirms building code compliance.", true,
        "https://www.houstontx.gov/planning/DevelopRegs/", "30–60 days", "$150–$600",
        ["Building inspection", "Fire inspection", "Zoning compliance"]),
      p("TABC Mixed Beverage Permit", "Texas Alcoholic Beverage Commission — Mixed Beverage Permit for full liquor on-premise. Requires surety bond.", false,
        "https://www.tabc.texas.gov/licenses-permits/", "30–60 days", "$4,050 + surety bond",
        ["TABC application", "Financial records", "Background check", "Surety bond required", "⚠️ Verify link: official .gov URLs may change"]),
      p("TABC Beer & Wine Permit (BG)", "Texas Alcoholic Beverage Commission permit for beer & wine only.", false,
        "https://www.tabc.texas.gov/licenses-permits/", "30–60 days", "$537.50/yr",
        ["TABC application", "Background check", "Proof of premises"]),
      p("Sign Permit", "Required for exterior signage.", true,
        "https://www.houstontx.gov/planning/", "15–30 days", "$75–$350",
        ["Sign design and dimensions", "Property owner approval"]),
    ]
  },

  "los angeles": {
    city: "Los Angeles", state: "CA", lastVerified: "2025",
    permits: [
      EIN(),
      p("California LLC / Corporation Registration", "File with CA Secretary of State. $800/yr franchise tax.", true,
        "https://www.sos.ca.gov/business-programs/business-entities/", "3–5 business days", "$70 (LLC) + $800 min franchise tax/yr",
        ["Articles of Organization", "Registered agent in CA"]),
      p("DBA (Fictitious Business Name)", "File with LA County Registrar-Recorder.", true,
        "https://lavote.gov/home/registrar/fictitious-business-names", "5–10 business days", "$26–$74",
        ["LLC registration", "Trade name", "Renews every 5 years"]),
      p("CA Seller's Permit", "Required to collect CA sales tax (10.25% in LA).", true,
        "https://www.cdtfa.ca.gov/services/#Register", "2–4 weeks", "FREE",
        ["EIN", "Business address", "Register via CDTFA"]),
      p("LA City Business Tax Certificate", "Based on gross receipts.", true,
        "https://finance.lacity.gov/business-tax", "10–20 business days", "$0–$1,000+/yr",
        ["Gross receipts estimate", "Business address", "Annual renewal by Jan 31"]),
      p("LA County Health Permit — Restaurant", "Full-service restaurants. Fee by seating capacity.", true,
        "http://ehservices.publichealth.lacounty.gov/", "30–60 days (inspection required)", "$731–$2,503/yr",
        ["Health department application", "Floor plan", "Menu", "Equipment list"]),
      p("Certificate of Occupancy", "Issued by LA Dept of Building & Safety (LADBS).", true,
        "https://www.ladbs.org/", "60–120 days", "$100–$700",
        ["Building inspection", "Fire inspection", "Zoning compliance", "ADA compliance"]),
      p("Conditional Use Permit (CUP) — Alcohol", "Required for alcohol service in most LA zones. Public hearing.", false,
        "https://planning.lacity.org/", "90–180 days (public hearing)", "$5,000–$20,000+",
        ["Land use application", "Public hearing attendance", "Neighbourhood notification"]),
      p("ABC License — Type 47 Full Liquor Restaurant", "California Department of Alcoholic Beverage Control — Type 47 licence for on-sale general (bona fide eating places). One-time fee + annual renewal.", false,
        "https://www.abc.ca.gov/licensing/", "3–6 months", "$13,800 one-time + $1,235/yr",
        ["ABC application", "Bona fide eating place requirement", "Background check", "One-time licence fee $13,800", "Annual renewal $1,235", "⚠️ Verify link: official .gov URLs may change"]),
      p("ABC License — Beer & Wine (Type 41)", "On-sale beer & wine for restaurants.", false,
        "https://www.abc.ca.gov/licensing/", "45–75 days", "$475/yr",
        ["ABC application", "Background check", "Proof of premises"]),
      p("Sign Permit", "Filed with LADBS.", true,
        "https://www.ladbs.org/", "15–30 days", "$100–$500",
        ["Sign design and dimensions", "Building owner approval"]),
    ]
  },

  "san francisco": {
    city: "San Francisco", state: "CA", lastVerified: "2025",
    permits: [
      EIN(),
      p("California LLC / Corporation Registration", "File with CA Secretary of State.", true,
        "https://www.sos.ca.gov/business-programs/business-entities/", "3–5 business days", "$70 (LLC) + $800/yr franchise tax",
        ["Articles of Organization", "Registered agent in CA"]),
      p("DBA (Fictitious Business Name)", "File with SF County Clerk.", true,
        "https://sfgov.org/countyclerk/fictitious-business-name-filings", "5–10 business days", "$62",
        ["LLC registration", "Trade name", "Renews every 5 years"]),
      p("CA Seller's Permit", "Required to collect CA sales tax.", true,
        "https://www.cdtfa.ca.gov/services/#Register", "2–4 weeks", "FREE",
        ["EIN", "Business address"]),
      p("SF Business Registration Certificate", "Required for all businesses operating in SF.", true,
        "https://sftreasurer.org/business/register-business", "5–10 business days", "$50–$500/yr",
        ["Business registration", "Location address"]),
      p("SF Health Permit — Food Facility", "Required for all food preparation and service.", true,
        "https://www.sfdph.org/dph/EH/Food/Permits.asp", "30–60 days (inspection required)", "$600–$1,500/yr",
        ["Health department application", "Floor plan", "Equipment list"]),
      p("Certificate of Occupancy", "Confirms building code compliance.", true,
        "https://sf.gov/departments/department-building-inspection", "60–120 days", "$200–$1,000",
        ["Building inspection", "Fire safety", "ADA compliance"]),
      p("ABC License — Type 47 Full Liquor Restaurant", "California Department of Alcoholic Beverage Control — Type 47 licence. One-time fee + annual renewal.", false,
        "https://www.abc.ca.gov/licensing/", "3–6 months", "$13,800 one-time + $1,235/yr",
        ["ABC application", "Bona fide eating place requirement", "Background check", "⚠️ Verify link: official .gov URLs may change"]),
      p("ABC License — Beer & Wine (Type 41)", "On-sale beer & wine.", false,
        "https://www.abc.ca.gov/licensing/", "45–75 days", "$475/yr",
        ["ABC application", "Background check"]),
    ]
  },

  chicago: {
    city: "Chicago", state: "IL", lastVerified: "2025",
    permits: [
      EIN(),
      p("Illinois LLC / Corporation Registration", "File with IL Secretary of State.", true,
        "https://www.ilsos.gov/departments/business_services/", "3–7 business days", "$150 (LLC)",
        ["Articles of Organization", "Registered agent in IL"]),
      p("DBA (Assumed Name)", "File with Cook County Clerk.", true,
        "https://www.cookcountyclerkil.gov/", "3–5 business days", "$25",
        ["LLC registration", "Trade name"]),
      p("Chicago Business License", "Required city business license.", true,
        "https://www.chicago.gov/city/en/depts/bacp/provdrs/licenseform.html", "15–30 days", "$250–$500/yr",
        ["Business registration", "Location address", "Annual renewal"]),
      p("Food Establishment License (CDPH)", "Required for all food service establishments.", true,
        "https://www.chicago.gov/city/en/depts/cdph/provdrs/healthy_restaurants.html", "30–60 days", "$660/yr (restaurant)",
        ["CDPH application", "Floor plan", "Menu", "Health inspection"]),
      p("Food Manager Certification", "At least 1 CFM on-site required.", true,
        "https://www.chicago.gov/city/en/depts/cdph/provdrs/food_manager_certification.html", "Exam: anytime", "$36 (exam)",
        ["Pass CDPH-approved exam", "Renews every 5 years"]),
      p("City of Chicago Liquor Licence — Restaurant", "Illinois Liquor Control Commission + City of Chicago liquor licence for restaurants with food as primary business.", false,
        "https://www.chicago.gov/city/en/depts/bacp/provdrs/liquor.html", "8–12 weeks", "$4,400–$6,000/yr",
        ["BACP application", "Background check", "Aldermanic approval", "51%+ food revenue", "⚠️ Verify link: official .gov URLs may change"]),
      p("City of Chicago Liquor Licence — Tavern", "For bars and nightclubs serving all alcohol.", false,
        "https://www.chicago.gov/city/en/depts/bacp/provdrs/liquor.html", "8–12 weeks", "$4,400–$8,800/yr",
        ["BACP application", "Background check", "Aldermanic approval"]),
      p("Sign Permit", "Required for exterior signage.", true,
        "https://www.chicago.gov/city/en/depts/bldgs/", "15–30 days", "$100–$500",
        ["Sign design", "Building owner approval"]),
    ]
  },

  boston: {
    city: "Boston", state: "MA", lastVerified: "2025",
    permits: [
      EIN(),
      p("Massachusetts LLC / Corporation Registration", "File with MA Secretary of Commonwealth.", true,
        "https://www.sec.state.ma.us/cor/coridx.htm", "3–5 business days", "$500 (LLC) / $275 (Corp)",
        ["Articles of Organization", "Registered agent in MA"]),
      p("DBA (Business Certificate)", "File with Boston City Clerk.", true,
        "https://www.boston.gov/departments/city-clerk", "5–10 business days", "$65",
        ["Business registration", "Trade name"]),
      p("MA Sales Tax Registration", "Required to collect 6.25% MA sales tax.", true,
        "https://www.mass.gov/how-to/register-your-business-with-mass-dor", "5–10 business days", "FREE",
        ["EIN", "Business address"]),
      p("Boston Food Establishment Permit (ISD)", "Required for all food service. Inspection required.", true,
        "https://www.boston.gov/departments/inspectional-services", "30–60 days", "$300–$600/yr",
        ["ISD application", "Floor plan", "Menu", "Equipment list"]),
      p("Food Manager Certification (ServSafe)", "At least 1 certified manager on-site.", true,
        "https://www.servsafe.com/", "Exam: anytime", "$100–$200",
        ["Pass ServSafe exam", "Renews every 5 years"]),
      p("Alcohol License — Beer & Wine", "Issued by Boston Licensing Board.", false,
        "https://www.boston.gov/departments/licensing-board", "60–120 days", "$1,500–$3,000/yr",
        ["Licensing board application", "Public hearing", "Background check"]),
      p("Alcohol License — All Alcohol", "Full liquor license. Very limited availability.", false,
        "https://www.boston.gov/departments/licensing-board", "90–180 days", "$3,000–$5,000/yr + market purchase",
        ["Must purchase from existing holder", "Licensing board hearing", "Background check"]),
    ]
  },

  austin: {
    city: "Austin", state: "TX", lastVerified: "2025",
    permits: [
      EIN(),
      p("Texas LLC / Corporation Registration", "File with TX Secretary of State.", true,
        "https://www.sos.state.tx.us/corp/forms_option.shtml", "3–5 business days", "$300 (LLC)",
        ["Articles of Organization", "Registered agent in TX"]),
      p("DBA (Assumed Name Certificate)", "File with Travis County Clerk.", true,
        "https://www.traviscountyclerk.org/", "2–5 business days", "$25",
        ["LLC registration", "Trade name"]),
      p("Texas Sales & Use Tax Permit", "Required to collect 8.25% TX sales tax.", true,
        "https://comptroller.texas.gov/taxes/sales/", "2–4 weeks", "FREE",
        ["EIN", "Business address"]),
      p("Austin Food Enterprise Permit", "City of Austin food establishment permit.", true,
        "https://www.austintexas.gov/department/food-enterprise-permits", "30–45 days", "$325–$825/yr",
        ["Austin Health application", "Floor plan", "Menu", "Equipment list"]),
      p("Food Manager Certification", "Texas-certified food manager.", true,
        "https://www.dshs.texas.gov/food-establishments/food-safety-training", "Exam: anytime", "$100–$200",
        ["Pass approved exam", "Renews every 5 years"]),
      p("TABC Mixed Beverage Permit", "Texas Alcoholic Beverage Commission — Mixed Beverage Permit for full liquor on-premise. Same as Houston — requires surety bond.", false,
        "https://www.tabc.texas.gov/licenses-permits/", "30–60 days", "$4,050 + surety bond",
        ["TABC application", "Financial records", "Background check", "Surety bond required", "⚠️ Verify link: official .gov URLs may change"]),
      p("TABC Beer & Wine Permit (BG)", "Beer & wine on-premise.", false,
        "https://www.tabc.texas.gov/licenses-permits/", "30–60 days", "$537.50/yr",
        ["TABC application", "Background check"]),
      p("Certificate of Occupancy", "Building code compliance.", true,
        "https://www.austintexas.gov/department/development-services", "30–60 days", "$100–$500",
        ["Building inspection", "Fire inspection"]),
    ]
  },

  nashville: {
    city: "Nashville", state: "TN", lastVerified: "2025",
    permits: [
      EIN(),
      p("Tennessee LLC / Corporation Registration", "File with TN Secretary of State.", true,
        "https://sos.tn.gov/business-services", "3–5 business days", "$300 (LLC) / $100 (Corp)",
        ["Articles of Organization", "Registered agent in TN"]),
      p("DBA (Assumed Name)", "File with Davidson County Register of Deeds.", true,
        "https://www.nashville.gov/departments/county-clerk", "3–5 business days", "$20",
        ["LLC registration", "Trade name"]),
      p("Tennessee Sales Tax Registration", "Required to collect 9.25% TN sales tax.", true,
        "https://www.tn.gov/revenue/taxes/sales-and-use-tax.html", "5–10 business days", "FREE",
        ["EIN", "Business address"]),
      p("Metro Nashville Business License", "Required for all businesses in Davidson County.", true,
        "https://www.nashville.gov/departments/finance/business-taxes", "10–20 business days", "$15–$100/yr",
        ["Business registration", "Location address", "Annual renewal"]),
      p("Metro Nashville Health Permit — Food Service", "Issued by Metro Public Health Department.", true,
        "https://www.nashville.gov/departments/health/environmental-health/food-protection", "30–60 days (inspection required)", "$350–$800/yr",
        ["Health department application", "Floor plan", "Menu", "Equipment list", "Passed health inspection"]),
      p("Food Manager Certification", "At least 1 certified food manager on-site.", true,
        "https://www.nashville.gov/departments/health", "Exam: anytime", "$100–$200",
        ["Pass approved exam (ServSafe)", "Renews every 5 years"]),
      p("Certificate of Occupancy", "Issued by Metro Nashville Codes Department.", true,
        "https://www.nashville.gov/departments/codes", "30–60 days", "$100–$500",
        ["Building inspection", "Fire inspection", "Zoning compliance"]),
      p("Alcohol License — Beer (On-Premise)", "Issued by Metro Nashville Beer Board.", false,
        "https://www.nashville.gov/departments/metropolitan-clerk/beer-board", "30–60 days", "$500–$1,000/yr",
        ["Beer Board application", "Background check", "Public hearing"]),
      p("Alcohol License — Liquor (Tennessee ABC)", "Tennessee Alcoholic Beverage Commission on-premise liquor licence.", false,
        "https://www.tn.gov/abc/licenses.html", "60–90 days", "$1,000–$3,000",
        ["TABC application", "Background check", "Financial disclosure", "Location must be in wet zone", "⚠️ Verify link: official .gov URLs may change"]),
      p("Entertainment Permit", "Required for live music or entertainment. Nashville-specific.", false,
        "https://www.nashville.gov/departments/codes", "30–60 days", "$200–$1,000",
        ["Entertainment application", "Noise level assessment", "Neighbourhood notification", "Separate from liquor license"]),
      p("Sign Permit", "Required for exterior signage.", true,
        "https://www.nashville.gov/departments/codes", "15–30 days", "$75–$400",
        ["Sign design", "Building owner approval"]),
    ]
  },

  // ─── UK CITIES ───────────────────────────────────────────────────
  london: {
    city: "London", state: "UK", lastVerified: "2025",
    permits: [
      p("Register as a Food Business", "Register with your local council at least 28 days before opening. It's free.", true,
        "https://www.gov.uk/food-business-registration", "28 days (mandatory notice period)", "FREE",
        ["Register 28 days before opening", "Local council application", "Details of food operations", "Premises address"]),
      p("Companies House Registration", "Register your limited company with Companies House.", true,
        "https://www.gov.uk/set-up-limited-company", "1–5 business days", "£12 (online) / £40 (postal)",
        ["Company name", "Registered office address", "Director details", "Share structure"]),
      p("Food Hygiene Rating", "Environmental Health Officer inspects your premises. Score of 0–5.", true,
        "https://www.food.gov.uk/business-guidance/food-hygiene-ratings", "Inspection after opening", "FREE (inspection)",
        ["Clean kitchen and food storage", "Food safety management system (HACCP)", "Staff hygiene training", "Temperature records"]),
      p("Level 2 Food Hygiene Certificate", "At least one person must hold a food hygiene certificate.", true,
        "https://www.food.gov.uk/business-guidance/safer-food-better-business", "1-day course", "£20–£100",
        ["Complete accredited course", "Pass assessment", "Renews every 3 years recommended"]),
      p("Premises Licence (Alcohol) — Licensing Act 2003", "Required for selling alcohol. Applied through local council under the Licensing Act 2003. Fee based on rateable value of premises.", false,
        "https://www.gov.uk/licence-alcohol-sales", "28 days minimum", "£100–£1,905 (based on rateable value)",
        ["Application to local council", "Designated Premises Supervisor (DPS) with personal licence", "Floor plan", "28-day public consultation period", "⚠️ Verify link: official .gov URLs may change"]),
      p("Personal Licence (Alcohol)", "Required for the Designated Premises Supervisor (DPS).", false,
        "https://www.gov.uk/personal-licence-holder-702", "2–4 weeks", "£37",
        ["Level 2 Award for Personal Licence Holders", "DBS criminal record check", "Two passport photos"]),
      p("Planning Permission / Change of Use", "May be needed if changing premises to restaurant use (A3/E class).", true,
        "https://www.gov.uk/planning-permission-england-wales", "8–13 weeks", "£462 (standard application)",
        ["Application to local planning authority", "Floor plans and elevations", "Neighbour notification"]),
      p("VAT Registration", "Mandatory when turnover exceeds £90,000. Register with HMRC.", true,
        "https://www.gov.uk/vat-registration", "Immediate to 30 days", "FREE (registration); 20% VAT on sales",
        ["Required above £90,000 turnover", "Register with HMRC", "File quarterly VAT returns", "Can reclaim VAT on business purchases"]),
      p("Business Rates", "Property tax paid to local council. Varies by location.", true,
        "https://www.gov.uk/introduction-to-business-rates", "Ongoing", "£1,000–£5,000/month in central London",
        ["Based on rateable value of premises", "Small business rate relief may apply", "Paid to local council"]),
      p("Employer's Liability Insurance", "Required by law if you have employees. £5M minimum cover.", true,
        "https://www.gov.uk/employers-liability-insurance", "Before hiring", "£400–£1,500/yr",
        ["£5M minimum cover required", "Display certificate at premises", "Criminal offence not to have it"]),
      p("Gas Safety Certificate", "Annual gas safety check for commercial kitchens.", true,
        "https://www.gassaferegister.co.uk/", "Annual", "£50–£200",
        ["Gas Safe registered engineer inspection", "Annual renewal", "Keep records for 2 years"]),
    ]
  },

  manchester: {
    city: "Manchester", state: "UK", lastVerified: "2025",
    permits: [
      p("Register as a Food Business", "Register with Manchester City Council at least 28 days before opening.", true,
        "https://www.manchester.gov.uk/info/200074/health/5730/food_business_registration", "28 days (mandatory notice period)", "FREE",
        ["Register 28 days before opening", "Manchester City Council application", "Details of food operations"]),
      p("Companies House Registration", "Register your limited company.", true,
        "https://www.gov.uk/set-up-limited-company", "1–5 business days", "£12 (online)",
        ["Company name", "Registered office address", "Director details"]),
      p("Food Hygiene Rating", "Environmental Health inspection. Score of 0–5.", true,
        "https://www.food.gov.uk/business-guidance/food-hygiene-ratings", "Inspection after opening", "FREE",
        ["Food safety management system", "Staff hygiene training", "Clean premises"]),
      p("Level 2 Food Hygiene Certificate", "At least one person must hold a food hygiene certificate.", true,
        "https://www.food.gov.uk/business-guidance/safer-food-better-business", "1-day course", "£20–£100",
        ["Complete accredited course", "Pass assessment"]),
      p("Premises Licence (Alcohol) — Licensing Act 2003", "Required for selling alcohol. Applied through Manchester City Council under the Licensing Act 2003.", false,
        "https://www.gov.uk/licence-alcohol-sales", "28 days minimum", "£100–£1,905 (based on rateable value)",
        ["DPS with personal licence", "Floor plan", "28-day consultation", "⚠️ Verify link: official .gov URLs may change"]),
      p("Personal Licence (Alcohol)", "For the Designated Premises Supervisor.", false,
        "https://www.gov.uk/personal-licence-holder-702", "2–4 weeks", "£37",
        ["Level 2 Award for Personal Licence Holders", "DBS check"]),
      p("VAT Registration", "Mandatory above £90,000 turnover.", true,
        "https://www.gov.uk/vat-registration", "Immediate to 30 days", "FREE; 20% VAT",
        ["Required above £90,000 turnover", "Quarterly VAT returns"]),
      p("Employer's Liability Insurance", "Required by law with employees.", true,
        "https://www.gov.uk/employers-liability-insurance", "Before hiring", "£400–£1,500/yr",
        ["£5M minimum cover", "Display certificate"]),
    ]
  },

  edinburgh: {
    city: "Edinburgh", state: "UK", lastVerified: "2025",
    permits: [
      p("Register as a Food Business", "Register with City of Edinburgh Council at least 28 days before opening.", true,
        "https://www.edinburgh.gov.uk/environmental-health", "28 days (mandatory notice period)", "FREE",
        ["Register 28 days before opening", "Edinburgh Council application", "Details of food operations"]),
      p("Companies House Registration", "Register your limited company.", true,
        "https://www.gov.uk/set-up-limited-company", "1–5 business days", "£12 (online)",
        ["Company name", "Registered office address", "Director details"]),
      p("Food Hygiene Rating", "Environmental Health inspection by Edinburgh Council.", true,
        "https://www.foodstandards.gov.scot/", "Inspection after opening", "FREE",
        ["Food safety management system", "Staff hygiene training", "Clean premises"]),
      p("Premises Licence — Licensing (Scotland) Act 2005", "Scottish licensing law is different from England. Applied through Edinburgh Licensing Board under the Licensing (Scotland) Act 2005.", false,
        "https://www.edinburgh.gov.uk/licences-permits", "6–10 weeks", "£200–£1,200",
        ["Application to Edinburgh Licensing Board", "Premises manager with personal licence", "Operating plan", "Layout plan", "Police Scotland consultation", "⚠️ Verify link: official .gov URLs may change"]),
      p("Personal Licence (Scotland)", "Required for premises manager. Scottish-specific qualification.", false,
        "https://www.edinburgh.gov.uk/licences-permits", "2–4 weeks", "£50",
        ["Scottish Certificate for Personal Licence Holders", "Police Scotland check", "Valid for 10 years"]),
      p("VAT Registration", "Mandatory above £90,000 turnover.", true,
        "https://www.gov.uk/vat-registration", "Immediate to 30 days", "FREE; 20% VAT",
        ["Required above £90,000 turnover", "Quarterly VAT returns"]),
      p("Employer's Liability Insurance", "Required by law with employees.", true,
        "https://www.gov.uk/employers-liability-insurance", "Before hiring", "£400–£1,500/yr",
        ["£5M minimum cover", "Display certificate"]),
      p("Planning Permission / Change of Use", "May be needed for change to restaurant use.", true,
        "https://www.edinburgh.gov.uk/planning-building", "8–12 weeks", "£401",
        ["Application to Edinburgh planning", "Floor plans", "Neighbour notification"]),
    ]
  },

  // ─── SPAIN ───────────────────────────────────────────────────────
  madrid: {
    city: "Madrid", state: "Spain", lastVerified: "2025",
    permits: [
      p("Registro Sanitario (Health Registration)", "Register your food business with local health authority.", true,
        "https://sede.madrid.es", "2–4 weeks", "FREE",
        ["Application to Ayuntamiento de Madrid", "Premises address", "Food activity description"]),
      p("Licencia de Actividad (Activity Licence)", "Municipal licence to operate a food establishment.", true,
        "https://sede.madrid.es", "1–3 months", "€300–€2,000",
        ["Application to Ayuntamiento", "Floor plans", "Technical project signed by architect", "Fire safety compliance"]),
      p("Alta en Hacienda (Tax Registration)", "Register with Spanish Tax Agency for business tax obligations.", true,
        "https://sede.agenciatributaria.gob.es/", "1–2 weeks", "FREE",
        ["NIE/NIF of business owner", "Business address", "Activity type (IAE code)"]),
      p("Licencia de Apertura (Opening Licence)", "Final opening authorization from city council.", true,
        "https://sede.madrid.es", "1–3 months", "€200–€1,500",
        ["All prior permits obtained", "Final inspection passed", "Insurance in place"]),
      p("Licencia de Venta de Bebidas Alcohólicas", "Alcohol sales licence through Ayuntamiento de Madrid. Included as part of activity licence with specific alcohol authorization.", false,
        "https://sede.madrid.es", "1–3 months", "€300–€1,500",
        ["Application to Ayuntamiento de Madrid", "Included in Licencia de Actividad with alcohol authorization", "Must specify alcohol sales in activity description", "⚠️ Verify link: official government URLs may change"]),
      p("Registro Manipuladores de Alimentos", "Food handler certification for all staff.", true,
        "https://sede.madrid.es", "1-day course", "€20–€50 per person",
        ["Complete accredited course", "All food handlers must be certified"]),
      p("IVA Registration (VAT)", "Register for Spanish VAT (IVA) — 10% for food, 21% standard.", true,
        "https://sede.agenciatributaria.gob.es/", "Immediate", "FREE (registration); 10%/21% IVA",
        ["Register via Agencia Tributaria", "Quarterly IVA returns"]),
    ]
  },

  barcelona: {
    city: "Barcelona", state: "Spain", lastVerified: "2025",
    permits: [
      p("Registro Sanitario (Health Registration)", "Register your food business with local health authority.", true,
        "https://ajuntament.barcelona.cat", "2–4 weeks", "FREE",
        ["Application to Ajuntament de Barcelona", "Premises address", "Food activity description"]),
      p("Licencia de Actividad (Activity Licence)", "Municipal licence from Ajuntament de Barcelona.", true,
        "https://ajuntament.barcelona.cat", "1–3 months", "€300–€2,000",
        ["Application to Ajuntament", "Floor plans", "Technical project", "Fire safety compliance"]),
      p("Alta en Hacienda (Tax Registration)", "Register with Spanish Tax Agency.", true,
        "https://sede.agenciatributaria.gob.es/", "1–2 weeks", "FREE",
        ["NIE/NIF", "Business address", "IAE code"]),
      p("Licencia de Venta de Bebidas Alcohólicas", "Alcohol sales licence through Ajuntament de Barcelona. Part of the activity licence with specific alcohol authorization.", false,
        "https://ajuntament.barcelona.cat", "1–3 months", "€300–€1,500",
        ["Application to Ajuntament de Barcelona", "Included in Licencia de Actividad with alcohol authorization", "Must specify alcohol sales in activity description", "⚠️ Verify link: official government URLs may change"]),
      p("Registro Manipuladores de Alimentos", "Food handler certification for all staff.", true,
        "https://ajuntament.barcelona.cat", "1-day course", "€20–€50 per person",
        ["Complete accredited course", "All food handlers must be certified"]),
      p("IVA Registration (VAT)", "Register for Spanish VAT (IVA).", true,
        "https://sede.agenciatributaria.gob.es/", "Immediate", "FREE (registration); 10%/21% IVA",
        ["Register via Agencia Tributaria", "Quarterly IVA returns"]),
    ]
  },

  valencia: {
    city: "Valencia", state: "Spain", lastVerified: "2025",
    permits: [
      p("Registro Sanitario (Health Registration)", "Register your food business with local health authority.", true,
        "https://www.valencia.es/", "2–4 weeks", "FREE",
        ["Application to Ayuntamiento de Valencia", "Premises address"]),
      p("Licencia de Actividad (Activity Licence)", "Municipal licence from Ayuntamiento de Valencia.", true,
        "https://www.valencia.es/", "1–3 months", "€300–€2,000",
        ["Floor plans", "Technical project", "Fire safety compliance"]),
      p("Licencia de Venta de Bebidas Alcohólicas", "Alcohol sales licence through Ayuntamiento de Valencia.", false,
        "https://www.valencia.es/", "1–3 months", "€300–€1,500",
        ["Application to Ayuntamiento de Valencia", "Included in Licencia de Actividad with alcohol authorization", "⚠️ Verify link: official government URLs may change"]),
      p("IVA Registration (VAT)", "Register for Spanish VAT (IVA).", true,
        "https://sede.agenciatributaria.gob.es/", "Immediate", "FREE; 10%/21% IVA",
        ["Register via Agencia Tributaria", "Quarterly IVA returns"]),
    ]
  },

  // ─── ITALY ───────────────────────────────────────────────────────
  milan: {
    city: "Milan", state: "Italy", lastVerified: "2025",
    permits: [
      p("SCIA — Segnalazione Certificata di Inizio Attività", "Main notification to start a food business. Filed with SUAP.", true,
        "https://www.comune.milano.it", "Immediate (self-certification)", "€100–€300",
        ["File with SUAP (Sportello Unico Attività Produttive)", "Self-certification of requirements", "Premises must meet hygiene standards"]),
      p("Autorizzazione Somministrazione Alimenti e Bevande", "Licence to serve food and beverages including alcohol. No separate alcohol licence needed — alcohol is included if specified in the main licence.", false,
        "https://www.comune.milano.it", "1–2 months", "€100–€500",
        ["Application to Comune di Milano", "SAB (ex-REC) professional qualification required", "Notify ASL (local health authority)", "Alcohol included — no separate licence needed", "⚠️ Verify link: official government URLs may change"]),
      p("SAB Professional Qualification (ex-REC)", "Required qualification to serve food & beverages in Italy.", true,
        "https://www.comune.milano.it", "Course: 100+ hours", "€500–€1,500 (course fee)",
        ["Complete accredited SAB course", "Pass exam", "Or prove 2+ years industry experience"]),
      p("ASL Health Notification", "Notify local health authority (ASL) of food business.", true,
        "https://www.ats-milano.it/", "2–4 weeks", "FREE",
        ["Premises hygiene compliance", "HACCP plan", "Staff health certificates"]),
      p("Partita IVA (VAT Number)", "Register for Italian VAT — 10% for food, 22% standard.", true,
        "https://www.agenziaentrate.gov.it/", "Immediate", "FREE; 10%/22% IVA",
        ["Register with Agenzia delle Entrate", "Quarterly VAT returns"]),
    ]
  },

  rome: {
    city: "Rome", state: "Italy", lastVerified: "2025",
    permits: [
      p("SCIA — Segnalazione Certificata di Inizio Attività", "Main notification to start a food business.", true,
        "https://www.comune.roma.it", "Immediate (self-certification)", "€100–€300",
        ["File with SUAP Roma Capitale", "Self-certification of requirements"]),
      p("Autorizzazione Somministrazione Alimenti e Bevande", "Licence to serve food and beverages including alcohol. No separate alcohol licence needed if included in main licence.", false,
        "https://www.comune.roma.it", "1–2 months", "€100–€500",
        ["Application to Comune di Roma", "SAB qualification required", "Notify ASL Roma", "Alcohol included — no separate licence needed", "⚠️ Verify link: official government URLs may change"]),
      p("SAB Professional Qualification (ex-REC)", "Required qualification to serve food & beverages.", true,
        "https://www.comune.roma.it", "Course: 100+ hours", "€500–€1,500",
        ["Complete accredited SAB course", "Pass exam", "Or prove 2+ years experience"]),
      p("ASL Health Notification", "Notify local health authority.", true,
        "https://www.aslroma1.it/", "2–4 weeks", "FREE",
        ["Premises hygiene compliance", "HACCP plan"]),
      p("Partita IVA (VAT Number)", "Register for Italian VAT.", true,
        "https://www.agenziaentrate.gov.it/", "Immediate", "FREE; 10%/22% IVA",
        ["Register with Agenzia delle Entrate"]),
    ]
  },

  florence: {
    city: "Florence", state: "Italy", lastVerified: "2025",
    permits: [
      p("SCIA — Segnalazione Certificata di Inizio Attività", "Main notification to start a food business.", true,
        "https://www.comune.fi.it/", "Immediate (self-certification)", "€100–€300",
        ["File with SUAP Firenze", "Self-certification of requirements"]),
      p("Autorizzazione Somministrazione Alimenti e Bevande", "Licence to serve food and beverages including alcohol. No separate alcohol licence needed.", false,
        "https://www.comune.fi.it/", "1–2 months", "€100–€500",
        ["Application to Comune di Firenze", "SAB qualification required", "Notify ASL Toscana Centro", "Alcohol included — no separate licence needed", "⚠️ Verify link: official government URLs may change"]),
      p("SAB Professional Qualification (ex-REC)", "Required qualification to serve food & beverages.", true,
        "https://www.comune.fi.it/", "Course: 100+ hours", "€500–€1,500",
        ["Complete accredited SAB course", "Pass exam"]),
      p("Partita IVA (VAT Number)", "Register for Italian VAT.", true,
        "https://www.agenziaentrate.gov.it/", "Immediate", "FREE; 10%/22% IVA",
        ["Register with Agenzia delle Entrate"]),
    ]
  },

  // ─── AUSTRALIA ───────────────────────────────────────────────────
  sydney: {
    city: "Sydney", state: "Australia", lastVerified: "2025",
    permits: [
      p("ABN Registration", "Register for an Australian Business Number.", true,
        "https://www.abr.gov.au/", "Immediate online", "FREE",
        ["Business details", "Apply via ABR"]),
      p("Food Business Notification (NSW Food Authority)", "Notify NSW Food Authority before opening.", true,
        "https://www.foodauthority.nsw.gov.au/", "Before opening", "FREE",
        ["Food business details", "Premises address"]),
      p("Council Development Application", "Local council approval for food premises.", true,
        "https://www.cityofsydney.nsw.gov.au/", "4–8 weeks", "A$500–A$2,000",
        ["Floor plans", "Fit-out specifications", "Neighbour notification"]),
      p("NSW Liquor Licence (Liquor & Gaming NSW)", "NSW liquor licence through Liquor & Gaming NSW (LGNSW). Various licence types available for restaurants and bars.", false,
        "https://www.liquorandgaming.nsw.gov.au/licences-and-permits/apply-for-a-liquor-licence", "4–8 weeks", "A$500–A$5,000",
        ["Application to Liquor & Gaming NSW", "RSA (Responsible Service of Alcohol) certification", "Community impact statement may be required", "⚠️ Verify link: official .gov URLs may change"]),
      p("Food Safety Supervisor Certificate", "At least one certified food safety supervisor required.", true,
        "https://www.foodauthority.nsw.gov.au/", "1-day course", "A$100–A$200",
        ["Complete accredited course", "Must be on-site during trading"]),
      p("GST Registration", "Register for GST if turnover exceeds A$75,000.", true,
        "https://www.ato.gov.au/", "Immediate", "FREE; 10% GST",
        ["Required above A$75,000 turnover", "Quarterly BAS returns"]),
    ]
  },

  melbourne: {
    city: "Melbourne", state: "Australia", lastVerified: "2025",
    permits: [
      p("ABN Registration", "Register for an Australian Business Number.", true,
        "https://www.abr.gov.au/", "Immediate online", "FREE",
        ["Business details", "Apply via ABR"]),
      p("Food Business Registration (City of Melbourne)", "Register with City of Melbourne council.", true,
        "https://www.melbourne.vic.gov.au/", "2–4 weeks", "A$300–A$600",
        ["Council application", "Premises address", "Food activity description"]),
      p("Victorian Liquor Licence (VCGLR)", "Victorian Commission for Gambling and Liquor Regulation licence for alcohol sales.", false,
        "https://www.vcglr.vic.gov.au/licences/liquor/apply-for-a-liquor-licence", "6–10 weeks", "A$500–A$3,000",
        ["Application to VCGLR", "RSA certification for all staff serving alcohol", "Premises plan", "⚠️ Verify link: official .gov URLs may change"]),
      p("Food Safety Supervisor Certificate", "At least one certified food safety supervisor required.", true,
        "https://www.health.vic.gov.au/", "1-day course", "A$100–A$200",
        ["Complete accredited course", "Must be on-site during trading"]),
      p("GST Registration", "Register for GST if turnover exceeds A$75,000.", true,
        "https://www.ato.gov.au/", "Immediate", "FREE; 10% GST",
        ["Required above A$75,000 turnover"]),
    ]
  },

  brisbane: {
    city: "Brisbane", state: "Australia", lastVerified: "2025",
    permits: [
      p("ABN Registration", "Register for an Australian Business Number.", true,
        "https://www.abr.gov.au/", "Immediate online", "FREE",
        ["Business details"]),
      p("Food Business Licence (Brisbane City Council)", "Register food business with Brisbane City Council.", true,
        "https://www.brisbane.qld.gov.au/", "2–4 weeks", "A$300–A$500",
        ["Council application", "Premises address"]),
      p("Queensland Liquor Licence (OLGR)", "Office of Liquor and Gaming Regulation Queensland licence for alcohol sales.", false,
        "https://www.justice.qld.gov.au/fair-and-safe-work/liquor/applying-for-a-licence", "4–8 weeks", "A$400–A$2,500",
        ["Application to OLGR Queensland", "RSA certification", "Premises plan", "⚠️ Verify link: official .gov URLs may change"]),
      p("Food Safety Supervisor Certificate", "At least one certified food safety supervisor.", true,
        "https://www.health.qld.gov.au/", "1-day course", "A$100–A$200",
        ["Complete accredited course"]),
      p("GST Registration", "Register for GST if turnover exceeds A$75,000.", true,
        "https://www.ato.gov.au/", "Immediate", "FREE; 10% GST",
        ["Required above A$75,000 turnover"]),
    ]
  },

  // ─── CANADA ──────────────────────────────────────────────────────
  toronto: {
    city: "Toronto", state: "Canada", lastVerified: "2025",
    permits: [
      p("Business Registration (Ontario)", "Register business with Ontario government.", true,
        "https://www.ontario.ca/page/register-business-name", "1–5 business days", "C$60–C$80",
        ["Business name registration", "HST number"]),
      p("DineSafe Food Handler Certification", "City of Toronto food handler certification required.", true,
        "https://www.toronto.ca/community-people/health-wellness-care/health-programs-advice/food-safety/dinesafe/", "1-day course", "C$50–C$100",
        ["Complete accredited course", "At least one certified handler per shift"]),
      p("Municipal Licensing (Food Premises)", "City of Toronto food premises licence.", true,
        "https://www.toronto.ca/", "4–8 weeks", "C$300–C$600",
        ["Toronto Public Health inspection", "Floor plan", "Menu"]),
      p("AGCO Liquor Sales Licence (Ontario)", "Alcohol and Gaming Commission of Ontario (AGCO) liquor sales licence for on-premises alcohol service.", false,
        "https://www.agco.ca/liquor/applying-liquor-licence-or-permit", "8–12 weeks", "C$1,500–C$3,000",
        ["Application to AGCO", "Smart Serve certification for all staff serving alcohol", "Municipal approval letter", "Floor plan showing service area", "⚠️ Verify link: official .gov URLs may change"]),
      p("HST Registration", "Register for HST if turnover exceeds C$30,000.", true,
        "https://www.canada.ca/en/revenue-agency.html", "Immediate", "FREE; 13% HST (Ontario)",
        ["Required above C$30,000 turnover", "Quarterly returns"]),
    ]
  },

  vancouver: {
    city: "Vancouver", state: "Canada", lastVerified: "2025",
    permits: [
      p("Business Registration (BC)", "Register business with BC government.", true,
        "https://www2.gov.bc.ca/gov/content/employment-business/business/register-business", "1–5 business days", "C$40–C$350",
        ["Business name registration"]),
      p("FOODSAFE Certification", "BC food safety certification required for all food handlers.", true,
        "https://www.foodsafe.ca/", "1-day course", "C$90–C$120",
        ["Complete FOODSAFE Level 1", "At least one certified per shift"]),
      p("City of Vancouver Business Licence", "Required for all businesses in Vancouver.", true,
        "https://vancouver.ca/doing-business/getting-a-licence.aspx", "2–4 weeks", "C$150–C$500",
        ["Application to City of Vancouver", "Premises address"]),
      p("BC Liquor Licence (LCRB)", "BC Liquor and Cannabis Regulation Branch licence for alcohol sales.", false,
        "https://www2.gov.bc.ca/gov/content/liquor-cannabis/businesses/liquor-licences-permits/apply", "10–16 weeks", "C$1,000–C$4,000",
        ["Application to LCRB", "Serving It Right certification for all staff", "Municipal approval", "Neighbourhood notification may be required", "⚠️ Verify link: official .gov URLs may change"]),
      p("GST Registration", "Register for GST if turnover exceeds C$30,000.", true,
        "https://www.canada.ca/en/revenue-agency.html", "Immediate", "FREE; 5% GST (BC)",
        ["Required above C$30,000 turnover"]),
    ]
  },

  // ─── UAE ─────────────────────────────────────────────────────────
  dubai: {
    city: "Dubai", state: "UAE", lastVerified: "2025",
    permits: [
      p("Trade Licence (DET)", "Dubai Economy & Tourism trade licence for food business.", true,
        "https://www.dubaided.gov.ae/", "1–2 weeks", "AED 10,000–AED 50,000",
        ["Activity type selection", "Trade name approval", "Premises lease agreement"]),
      p("Food Safety Permit (Dubai Municipality)", "Dubai Municipality food safety permit.", true,
        "https://www.dm.gov.ae/", "2–4 weeks", "AED 2,000–AED 5,000",
        ["HACCP compliance", "Kitchen layout plan", "Staff health cards"]),
      p("Alcohol Licence (Dubai Tourism — DTCM)", "Dubai Tourism alcohol licence. ⚠️ EXTREMELY RESTRICTED — only available for hotels, hotel restaurants, and specifically licensed venues. Most standalone restaurants CANNOT get an alcohol licence in Dubai.", false,
        "https://www.dtcm.gov.ae", "4–8 weeks", "AED 20,000–AED 50,000/yr",
        ["⚠️ WARNING: Only available for hotels and licensed venues", "Most standalone restaurants operate WITHOUT alcohol", "Application through Dubai Tourism (DTCM)", "Hotel or licensed venue affiliation required", "Annual renewal", "⚠️ Verify link: official government URLs may change"]),
      p("Staff Health Cards", "Medical fitness certificates for all food handlers.", true,
        "https://www.dm.gov.ae/", "1–2 weeks", "AED 200–AED 500 per person",
        ["Medical examination at approved centre", "Required for all food handlers"]),
    ]
  },

  "abu dhabi": {
    city: "Abu Dhabi", state: "UAE", lastVerified: "2025",
    permits: [
      p("Trade Licence (ADDED)", "Abu Dhabi Department of Economic Development trade licence.", true,
        "https://www.added.gov.ae/", "1–2 weeks", "AED 10,000–AED 50,000",
        ["Activity type selection", "Trade name approval", "Premises lease"]),
      p("Food Safety Permit (ADFCA)", "Abu Dhabi Agriculture & Food Safety Authority permit.", true,
        "https://www.adfca.gov.ae/", "2–4 weeks", "AED 2,000–AED 5,000",
        ["HACCP compliance", "Kitchen layout plan", "Staff health cards"]),
      p("Alcohol Licence (Abu Dhabi)", "⚠️ EXTREMELY RESTRICTED — only available for hotels and specifically licensed venues. Most standalone restaurants operate without alcohol.", false,
        "https://www.added.gov.ae/", "4–8 weeks", "AED 20,000–AED 50,000/yr",
        ["⚠️ WARNING: Only for hotels and licensed venues", "Most restaurants operate WITHOUT alcohol", "Special authorization required", "⚠️ Verify link: official government URLs may change"]),
    ]
  },

  // ─── SINGAPORE ───────────────────────────────────────────────────
  singapore: {
    city: "Singapore", state: "Singapore", lastVerified: "2025",
    permits: [
      p("ACRA Business Registration", "Register business with Accounting and Corporate Regulatory Authority.", true,
        "https://www.acra.gov.sg/", "1–2 days", "S$300–S$600",
        ["Business name approval", "Director details", "Registered address"]),
      p("SFA Food Shop Licence", "Singapore Food Agency licence for food establishments.", true,
        "https://www.sfa.gov.sg/", "2–4 weeks", "S$200–S$500",
        ["SFA application via GoBusiness", "Premises layout plan", "NEA approval"]),
      p("SFA Liquor Licence", "Singapore Food Agency liquor licence. Fee depends on operating hours.", false,
        "https://licence1.business.gov.sg", "2–4 weeks", "S$110–S$880/yr (depending on hours)",
        ["Application via GoBusiness Licensing", "Valid food shop licence required first", "Fee based on operating hours", "⚠️ Verify link: official government URLs may change"]),
      p("Food Hygiene Officer", "At least one certified food hygiene officer per premises.", true,
        "https://www.sfa.gov.sg/", "Course: 1–2 days", "S$150–S$300",
        ["Complete WSQ Food Safety Course Level 1", "Must be on-site during operations"]),
      p("GST Registration", "Register for GST if turnover exceeds S$1 million.", true,
        "https://www.iras.gov.sg/", "1–2 weeks", "FREE; 9% GST",
        ["Required above S$1M turnover", "Quarterly returns"]),
    ]
  },

  // ─── BRAZIL ──────────────────────────────────────────────────────
  "são paulo": {
    city: "São Paulo", state: "Brazil", lastVerified: "2025",
    permits: [
      p("CNPJ Registration", "Register business with Receita Federal (Brazilian IRS).", true,
        "https://www.gov.br/receitafederal/", "1–2 weeks", "FREE",
        ["CPF of business owner", "Business address", "Activity type (CNAE code)"]),
      p("Alvará de Funcionamento", "Municipal operating licence from Prefeitura de São Paulo. This is the main business licence — alcohol sales are covered under this licence (no separate alcohol permit needed).", true,
        "https://www.prefeitura.sp.gov.br", "2–4 weeks", "R$200–R$1,000",
        ["Application to Prefeitura de São Paulo", "CNPJ registration", "Premises address", "Fire safety certificate (AVCB)", "Alcohol sales covered — no separate licence needed", "⚠️ Verify link: official government URLs may change"]),
      p("Vigilância Sanitária (ANVISA Health Permit)", "Health inspection and permit from COVISA São Paulo.", true,
        "https://www.prefeitura.sp.gov.br/covisa", "2–4 weeks", "R$100–R$500",
        ["Premises hygiene inspection", "Staff health certificates", "HACCP plan"]),
      p("AVCB (Fire Safety Certificate)", "Auto de Vistoria do Corpo de Bombeiros — fire safety certificate.", true,
        "https://www.bombeiros.sp.gov.br/", "4–8 weeks", "R$500–R$2,000",
        ["Fire safety equipment", "Emergency exits", "Fire brigade inspection"]),
      p("Inscrição Estadual (State Tax)", "Register for ICMS (state sales tax).", true,
        "https://portal.fazenda.sp.gov.br/", "1–2 weeks", "FREE",
        ["CNPJ required", "State tax obligations"]),
    ]
  },
  "sao paulo": {
    city: "São Paulo", state: "Brazil", lastVerified: "2025",
    get permits() { return CITY_PERMITS["são paulo"].permits; }
  },
};

// Country-level generic permit templates for cities not in the database
const COUNTRY_GENERIC_PERMITS: Record<string, CityPermitConfig> = {
  "united states": {
    city: "", state: "US", lastVerified: "2025",
    permits: [
      EIN(),
      p("State LLC / Business Registration", "Register your business entity with your state.", true,
        "https://www.sba.gov/business-guide/launch-your-business/register-your-business", "3–10 business days", "$50–$500",
        ["Articles of Organization", "Registered agent", "Filing fee"]),
      p("Sales Tax Permit", "Required to collect state and local sales tax.", true,
        "https://www.sba.gov/business-guide/manage-your-business/pay-taxes", "Instant to 2 weeks", "FREE in most states",
        ["EIN", "Business address"]),
      p("Food Service License (FDA Compliant)", "Required by your county health department for all food service.", true,
        "https://www.fda.gov/food/retail-food-industrie/retail-food-protection", "30–60 days", "$200–$1,000/yr",
        ["Health department application", "Floor plan", "Menu", "Equipment list", "Health inspection"]),
      p("Food Manager Certification (ServSafe / ANSI)", "At least 1 certified food manager on-site at all times. FDA Food Code requirement.", true,
        "https://www.servsafe.com/", "Exam: anytime", "$100–$200",
        ["Pass approved food safety exam (ServSafe, ANSI-accredited)", "Renews every 5 years"]),
      p("Certificate of Occupancy", "Confirms building meets fire, safety, and zoning codes.", true,
        "https://www.sba.gov/business-guide/launch-your-business/apply-for-licenses-and-permits", "30–90 days", "$100–$700",
        ["Building inspection", "Fire inspection", "Zoning compliance", "ADA compliance"]),
      p("Health Department Permit", "County/city health department pre-opening inspection.", true,
        "https://www.cdc.gov/nceh/ehs/activities/food.html", "30–45 days", "$200–$500/yr",
        ["Facility layout plans", "Handwashing stations", "Equipment list", "Pest control plan"]),
      p("Fire Safety Inspection", "Fire department checks safety systems before opening.", true,
        "https://www.usfa.fema.gov/prevention/outreach/", "30–60 days", "$50–$500",
        ["Fire alarm system", "Exit signage", "Fire extinguishers", "Occupancy capacity posted"]),
      p("Alcohol License — Beer & Wine", "State-issued beer & wine license for on-premise consumption.", false,
        "https://www.ttb.gov/alcohol/beverage-alcohol", "45–90 days", "$300–$1,500/yr",
        ["State alcohol board application", "Background check", "Proof of premises"]),
      p("Alcohol License — Full Liquor", "Full liquor license for spirits and cocktails.", false,
        "https://www.ttb.gov/alcohol/beverage-alcohol", "60–120 days", "$1,000–$14,000+/yr",
        ["State alcohol board application", "Background check", "Financial disclosure"]),
      p("Sign Permit", "Required for exterior business signage.", true,
        "https://www.sba.gov/business-guide/launch-your-business/apply-for-licenses-and-permits", "15–30 days", "$75–$500",
        ["Sign design with dimensions", "Property owner approval"]),
    ]
  },
  "united kingdom": {
    city: "", state: "UK", lastVerified: "2025",
    permits: [
      p("Register as a Food Business (FSA)", "Register with your local council at least 28 days before opening. Required by the Food Standards Agency.", true,
        "https://www.gov.uk/food-business-registration", "28 days (mandatory notice period)", "FREE",
        ["Register 28 days before opening", "Local council application", "Details of food operations"]),
      p("Companies House Registration", "Register your limited company with Companies House.", true,
        "https://www.gov.uk/set-up-limited-company", "1–5 business days", "£12 (online)",
        ["Company name", "Registered office address", "Director details"]),
      p("Food Hygiene Rating", "Environmental Health Officer inspects your premises. Score of 0–5.", true,
        "https://www.food.gov.uk/business-guidance/food-hygiene-ratings", "Inspection after opening", "FREE",
        ["Food safety management system (HACCP)", "Staff hygiene training", "Temperature records"]),
      p("Level 2 Food Hygiene Certificate", "At least one person must hold a food hygiene certificate.", true,
        "https://www.food.gov.uk/business-guidance/safer-food-better-business", "1-day course", "£20–£100",
        ["Complete accredited course", "Pass assessment"]),
      p("Premises Licence (Alcohol) — Licensing Act 2003", "Required for selling alcohol. Applied through local council.", false,
        "https://www.gov.uk/licence-alcohol-sales", "28 days minimum", "£100–£1,905",
        ["Application to local council", "DPS with personal licence", "Floor plan", "28-day public consultation"]),
      p("Personal Licence (Alcohol)", "Required for the Designated Premises Supervisor.", false,
        "https://www.gov.uk/personal-licence-holder-702", "2–4 weeks", "£37",
        ["Level 2 Award for Personal Licence Holders", "DBS criminal record check"]),
      p("VAT Registration", "Mandatory when turnover exceeds £90,000.", true,
        "https://www.gov.uk/vat-registration", "Immediate to 30 days", "FREE; 20% VAT",
        ["Required above £90,000 turnover", "Quarterly VAT returns"]),
      p("Employer's Liability Insurance", "Required by law if you have employees. £5M minimum cover.", true,
        "https://www.gov.uk/employers-liability-insurance", "Before hiring", "£400–£1,500/yr",
        ["£5M minimum cover required", "Display certificate at premises"]),
    ]
  },
  italy: {
    city: "", state: "Italy", lastVerified: "2025",
    permits: [
      p("SCIA — Segnalazione Certificata di Inizio Attività", "Main notification to start a food business. Filed with SUAP (Sportello Unico Attività Produttive).", true,
        "https://www.governo.it/", "Immediate (self-certification)", "€100–€300",
        ["File with SUAP", "Self-certification of requirements", "Premises must meet hygiene standards"]),
      p("NIA — Notifica Inizio Attività (ASL)", "Health notification to local ASL (health authority). HACCP plan required.", true,
        "https://www.salute.gov.it/", "2–4 weeks", "FREE",
        ["Premises hygiene compliance", "HACCP plan", "Staff health certificates"]),
      p("Autorizzazione Sanitaria", "Sanitary authorization from local health authority for food preparation.", true,
        "https://www.salute.gov.it/", "2–4 weeks", "FREE–€200",
        ["Kitchen meets hygiene standards", "HACCP plan in place", "Staff trained in food safety"]),
      p("SAB Professional Qualification (ex-REC)", "Required qualification to serve food & beverages in Italy.", true,
        "https://www.governo.it/", "Course: 100+ hours", "€500–€1,500",
        ["Complete accredited SAB course", "Pass exam", "Or prove 2+ years industry experience"]),
      p("Autorizzazione Somministrazione Alimenti e Bevande", "Licence to serve food and beverages including alcohol.", false,
        "https://www.governo.it/", "1–2 months", "€100–€500",
        ["Application to Comune (municipality)", "SAB qualification required", "Notify ASL", "Alcohol included — no separate licence needed"]),
      p("Partita IVA (VAT Number)", "Register for Italian VAT — 10% for food, 22% standard.", true,
        "https://www.agenziaentrate.gov.it/", "Immediate", "FREE; 10%/22% IVA",
        ["Register with Agenzia delle Entrate", "Quarterly VAT returns"]),
    ]
  },
  "united arab emirates": {
    city: "", state: "UAE", lastVerified: "2025",
    permits: [
      p("Trade Licence", "Municipality trade licence for food business.", true,
        "https://www.government.ae/", "1–2 weeks", "AED 10,000–AED 50,000",
        ["Activity type selection", "Trade name approval", "Premises lease agreement"]),
      p("Municipality Food Safety Permit", "Municipality food safety permit with HACCP requirements.", true,
        "https://www.government.ae/", "2–4 weeks", "AED 2,000–AED 5,000",
        ["HACCP compliance", "Kitchen layout plan", "Staff health cards"]),
      p("Staff Health Cards", "Medical fitness certificates for all food handlers.", true,
        "https://www.government.ae/", "1–2 weeks", "AED 200–AED 500 per person",
        ["Medical examination at approved centre", "Required for all food handlers"]),
      p("Alcohol Licence", "⚠️ EXTREMELY RESTRICTED — only available for hotels and specifically licensed venues.", false,
        "https://www.government.ae/", "4–8 weeks", "AED 20,000–AED 50,000/yr",
        ["⚠️ Only for hotels and licensed venues", "Most restaurants operate WITHOUT alcohol", "Special authorization required"]),
    ]
  },
  spain: {
    city: "", state: "Spain", lastVerified: "2025",
    permits: [
      p("Registro Sanitario (Health Registration)", "Register your food business with local health authority.", true,
        "https://www.mscbs.gob.es/", "2–4 weeks", "FREE",
        ["Application to Ayuntamiento (city council)", "Premises address"]),
      p("Licencia de Actividad / Apertura", "Municipal operating licence from Ayuntamiento.", true,
        "https://www.mscbs.gob.es/", "1–3 months", "€300–€3,000",
        ["Floor plans and technical project", "Fire safety compliance", "Environmental compliance"]),
      p("Registro Manipuladores de Alimentos", "Food handler certification for all staff.", true,
        "https://www.mscbs.gob.es/", "1-day course", "€20–€50 per person",
        ["Complete accredited course", "All food handlers must be certified"]),
      p("Licencia de Venta de Bebidas Alcohólicas", "Alcohol sales licence through Ayuntamiento.", false,
        "https://www.mscbs.gob.es/", "1–3 months", "€300–€2,000",
        ["Application to Ayuntamiento", "May be included in Licencia de Actividad"]),
      p("IVA Registration (VAT)", "Register for Spanish VAT (IVA).", true,
        "https://sede.agenciatributaria.gob.es/", "Immediate", "FREE; 10%/21% IVA",
        ["Register via Agencia Tributaria", "Quarterly IVA returns"]),
    ]
  },
  australia: {
    city: "", state: "Australia", lastVerified: "2025",
    permits: [
      p("ABN Registration", "Register for an Australian Business Number.", true,
        "https://www.abr.gov.au/", "Immediate online", "FREE",
        ["Business details", "Apply via ABR"]),
      p("Food Business Registration (Local Council)", "Register your food business with your local council.", true,
        "https://www.foodstandards.gov.au/", "2–4 weeks", "A$300–A$600",
        ["Council application", "Premises address", "Food activity description"]),
      p("Food Safety Supervisor Certificate", "At least one certified food safety supervisor required.", true,
        "https://www.foodstandards.gov.au/", "1-day course", "A$100–A$200",
        ["Complete accredited course", "Must be on-site during trading"]),
      p("Liquor Licence (State Authority)", "State-issued liquor licence for alcohol service.", false,
        "https://www.australia.gov.au/", "4–10 weeks", "A$500–A$5,000",
        ["Application to state liquor authority", "RSA certification", "Community impact statement may be required"]),
      p("GST Registration", "Register for GST if turnover exceeds A$75,000.", true,
        "https://www.ato.gov.au/", "Immediate", "FREE; 10% GST",
        ["Required above A$75,000 turnover", "Quarterly BAS returns"]),
    ]
  },
  canada: {
    city: "", state: "Canada", lastVerified: "2025",
    permits: [
      p("Business Registration (Provincial)", "Register business with your provincial government.", true,
        "https://www.canada.ca/en/services/business.html", "1–5 business days", "C$40–C$350",
        ["Business name registration", "Provincial registration"]),
      p("Food Handler Certification", "Provincial food handler certification required.", true,
        "https://www.canada.ca/en/health-canada/services/food-safety.html", "1-day course", "C$50–C$120",
        ["Complete accredited course", "At least one certified handler per shift"]),
      p("Municipal Food Premises Licence", "City-issued food premises licence.", true,
        "https://www.canada.ca/en/services/business.html", "4–8 weeks", "C$300–C$600",
        ["Public health inspection", "Floor plan", "Menu"]),
      p("Liquor Licence (Provincial)", "Provincial liquor licence for alcohol service.", false,
        "https://www.canada.ca/en/services/business.html", "8–16 weeks", "C$1,000–C$4,000",
        ["Application to provincial liquor authority", "Responsible service certification for staff"]),
      p("HST/GST Registration", "Register for HST/GST if turnover exceeds C$30,000.", true,
        "https://www.canada.ca/en/revenue-agency.html", "Immediate", "FREE; 5–15% HST/GST",
        ["Required above C$30,000 turnover", "Quarterly returns"]),
    ]
  },
  singapore: {
    city: "", state: "Singapore", lastVerified: "2025",
    permits: [
      p("ACRA Business Registration", "Register business with ACRA.", true,
        "https://www.acra.gov.sg/", "1–2 days", "S$300–S$600",
        ["Business name approval", "Director details"]),
      p("SFA Food Shop Licence", "Singapore Food Agency licence for food establishments.", true,
        "https://www.sfa.gov.sg/", "2–4 weeks", "S$200–S$500",
        ["SFA application via GoBusiness", "Premises layout plan"]),
      p("SFA Liquor Licence", "Liquor licence. Fee depends on operating hours.", false,
        "https://licence1.business.gov.sg", "2–4 weeks", "S$110–S$880/yr",
        ["Valid food shop licence required first", "Fee based on operating hours"]),
      p("GST Registration", "Register for GST if turnover exceeds S$1 million.", true,
        "https://www.iras.gov.sg/", "1–2 weeks", "FREE; 9% GST",
        ["Required above S$1M turnover"]),
    ]
  },
  brazil: {
    city: "", state: "Brazil", lastVerified: "2025",
    permits: [
      p("CNPJ Registration", "Register business with Receita Federal.", true,
        "https://www.gov.br/receitafederal/", "1–2 weeks", "FREE",
        ["CPF of business owner", "Business address", "Activity type (CNAE code)"]),
      p("Alvará de Funcionamento", "Municipal operating licence. Alcohol sales typically covered under this licence.", true,
        "https://www.gov.br/", "2–4 weeks", "R$200–R$1,000",
        ["CNPJ registration", "Premises address", "Fire safety certificate (AVCB)"]),
      p("Vigilância Sanitária (Health Permit)", "Health inspection and permit from local health authority.", true,
        "https://www.gov.br/", "2–4 weeks", "R$100–R$500",
        ["Premises hygiene inspection", "Staff health certificates", "HACCP plan"]),
      p("AVCB (Fire Safety Certificate)", "Fire safety certificate from fire brigade.", true,
        "https://www.gov.br/", "4–8 weeks", "R$500–R$2,000",
        ["Fire safety equipment", "Emergency exits", "Fire brigade inspection"]),
    ]
  },
};

/**
 * Detect country from city name using location data or the CITY_PERMITS state field.
 */
function detectCountry(cityName: string): string | null {
  const key = cityName.toLowerCase().trim();
  const config = CITY_PERMITS[key];
  if (config) {
    const state = config.state.toLowerCase();
    if (["fl","ny","tx","ca","il","ma","co","dc","wa","ga","pa","or","nc","tn","nv","az","oh","mi","mn","mo","wi","la","sc","hi"].includes(state) || state === "us") return "united states";
    if (state === "uk") return "united kingdom";
    if (state === "italy") return "italy";
    if (state === "spain") return "spain";
    if (state === "australia") return "australia";
    if (state === "canada") return "canada";
    if (state === "uae") return "united arab emirates";
    if (state === "singapore") return "singapore";
    if (state === "brazil") return "brazil";
  }
  return null;
}

export function getCityPermitConfig(cityName: string): CityPermitConfig | null {
  const key = cityName.toLowerCase().trim();
  if (CITY_PERMITS[key]) return CITY_PERMITS[key];
  
  // Try to find a country-level fallback based on the location data
  // Import dynamically would be circular, so we detect from known patterns
  return null;
}

/**
 * Get country-generic permits for a given country name.
 */
export function getCountryGenericPermits(country: string): CityPermitConfig | null {
  const key = country.toLowerCase().trim();
  // Handle common variations
  const mapping: Record<string, string> = {
    "us": "united states", "usa": "united states", "united states": "united states",
    "uk": "united kingdom", "united kingdom": "united kingdom", "england": "united kingdom", "great britain": "united kingdom",
    "italy": "italy", "italia": "italy",
    "spain": "spain", "españa": "spain",
    "australia": "australia",
    "canada": "canada",
    "uae": "united arab emirates", "united arab emirates": "united arab emirates",
    "singapore": "singapore",
    "brazil": "brazil", "brasil": "brazil",
  };
  const mapped = mapping[key];
  return mapped ? COUNTRY_GENERIC_PERMITS[mapped] || null : null;
}

export function getCityPermits(cityName: string, alcoholType: string, isFoodBusiness: boolean): PermitDetail[] {
  const config = getCityPermitConfig(cityName);
  if (!config) return [];

  return config.permits.filter(p => {
    const nameLower = p.name.toLowerCase();
    const isAlcohol = nameLower.includes("alcohol") || nameLower.includes("liquor") ||
                      nameLower.includes("tabc") || nameLower.includes("abc license") || nameLower.includes("abc licence") ||
                      nameLower.includes("conditional use permit") ||
                      nameLower.includes("premises licence") || nameLower.includes("personal licence") ||
                      nameLower.includes("bebidas alcohólicas") || nameLower.includes("somministrazione") ||
                      nameLower.includes("agco") || nameLower.includes("lcrb") || nameLower.includes("vcglr") ||
                      nameLower.includes("olgr") || nameLower.includes("sfa liquor");

    if (isAlcohol) {
      if (alcoholType === "none") return false;
      if (alcoholType === "beer-wine") {
        const isBeerWine = nameLower.includes("beer") || nameLower.includes("wine") || nameLower.includes("2aps") || nameLower.includes("bg permit") || nameLower.includes("type 41");
        return isBeerWine;
      }
      return true;
    }

    const isFoodPermit = nameLower.includes("food") || nameLower.includes("health permit") || nameLower.includes("sanitario") || nameLower.includes("vigilância");
    if (isFoodPermit && !isFoodBusiness) return false;

    return true;
  });
}
